import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Box, Container as MuiContainer, CssBaseline, Paper as MuiPaper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { spacing } from '@mui/system';

import GlobalStyle from '../components/GlobalStyle';
import Navbar from '../components/navbar/Navbar';
import Sidebar from '../components/sidebar/Sidebar';
import Footer from '../components/Footer';
import ThemePicker from '../components/ThemePicker';
import AuthGuard from '../components/guards/AuthGuard';
import MessageToast from '../components/common/MessageToast';
import { useSessionStore } from '../stores';
import { NavRoute, routes } from '../routes';

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const Container = styled(MuiContainer)`
  height: 100%;
`;

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sideNavItems, setSideNavItems] = useState<NavRoute[]>([]);
  const roles = useSessionStore.use.roles();
  const hints = useSessionStore.use.hints();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = routes.find((route) => route?.path === '/')!.children!.filter((child) => child.priority || child.accessPath);

  const canAccessRoute = (authorizedRoutes: NavRoute[], currentPath: string) => {
    const matchingRoute = authorizedRoutes.find(
      (x) => (x.path === currentPath || (x.accessPath && currentPath.startsWith(x.accessPath))) && (!x.role || roles.includes(x.role)),
    );
    if (matchingRoute) return true;

    return authorizedRoutes.some(
      (x) => (x.path === currentPath || (x.accessPath && currentPath.startsWith(x.accessPath))) && (!x.role || roles.includes(x.role)),
    );
  };

  const findPreferredRoute = (authorizedRoutes: NavRoute[]) => {
    const validInitialRoutes = authorizedRoutes.filter((x) => x.priority).sort((x, y) => x.priority! - y.priority!);
    const hintedRoute = validInitialRoutes.find((x) => x.hint && hints.includes(x.hint));
    return hintedRoute ?? validInitialRoutes[0];
  };

  const getAuthorizedRoutes = () => {
    return navItems.filter((x) => !x.role || roles.includes(x.role));
  };

  const navigateToPreferredRoute = () => {
    const authorizedRoutes = getAuthorizedRoutes();
    if (!canAccessRoute(authorizedRoutes, location.pathname.substring(1))) {
      //Find the first item the user can access
      const preferredRoute = findPreferredRoute(authorizedRoutes);
      navigate(preferredRoute.path);
    }
  };

  const setupSideNav = () => {
    const authorizedRoutes = getAuthorizedRoutes();
    const sideNavItems = authorizedRoutes.filter((x) => x.sideNav);
    setSideNavItems(sideNavItems);
  };

  // Navigation is impacted by roles
  useEffect(() => {
    navigateToPreferredRoute();
    setupSideNav();
  }, [roles]);

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      navigateToPreferredRoute();
    }
  }, [location.pathname]);

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Root>
      <AuthGuard>
        <CssBaseline />
        <GlobalStyle />
        <Drawer>
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <Sidebar
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              items={sideNavItems}
            />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Sidebar PaperProps={{ style: { width: drawerWidth } }} items={sideNavItems} />
          </Box>
        </Drawer>
        <AppContent>
          <MessageToast></MessageToast>
          <Navbar onDrawerToggle={handleDrawerToggle} />
          <MainContent pt={isSmUp ? 5 : 3} pb={isSmUp ? 5 : 2} px={isSmUp ? 3 : 0}>
            <Container maxWidth="xl">
              {children}
              <Outlet />
            </Container>
          </MainContent>
          <Footer />
        </AppContent>
        <ThemePicker />
      </AuthGuard>
    </Root>
  );
};

export default Dashboard;
